import { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import fr from "./locales/fr";
import en from "./locales/en";
import pl from "./locales/pl";

const LOCALES = { fr, en, pl };
export const SUPPORTED_LANGS = ["fr", "en", "pl"];
export const DEFAULT_LANG = "fr";

const I18nContext = createContext(null);

// Detect best lang from URL > localStorage > Accept-Language > default
export function detectLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  const pathLang = window.location.pathname.split("/")[1];
  if (SUPPORTED_LANGS.includes(pathLang)) return pathLang;
  const saved = localStorage.getItem("hejpoland_lang");
  if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
  const browser = (navigator.language || "fr").slice(0, 2).toLowerCase();
  if (SUPPORTED_LANGS.includes(browser)) return browser;
  return DEFAULT_LANG;
}

// Resolve nested dotted key e.g. "hero.title"
function resolve(obj, key) {
  return key.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const pathLang = pathSegments[0];
  const lang = SUPPORTED_LANGS.includes(pathLang) ? pathLang : DEFAULT_LANG;

  useEffect(() => {
    if (SUPPORTED_LANGS.includes(pathLang)) {
      localStorage.setItem("hejpoland_lang", pathLang);
      document.documentElement.lang = pathLang;
    }
  }, [pathLang]);

  const value = useMemo(() => {
    const locale = LOCALES[lang] || LOCALES[DEFAULT_LANG];

    const t = (key, vars = {}) => {
      let val = resolve(locale, key);
      if (val === undefined) val = resolve(LOCALES[DEFAULT_LANG], key);
      if (val === undefined) return key;
      if (typeof val === "string" && vars) {
        return val.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : `{${k}}`));
      }
      return val;
    };

    // Build localized path: lp("/logement") -> "/fr/logement"
    const lp = (path = "") => {
      const clean = path.startsWith("/") ? path : `/${path}`;
      return `/${lang}${clean === "/" ? "/" : clean}`;
    };

    // Change language while preserving current sub-path
    const setLang = (newLang) => {
      if (!SUPPORTED_LANGS.includes(newLang)) return;
      const rest = pathSegments.slice(SUPPORTED_LANGS.includes(pathLang) ? 1 : 0).join("/");
      const target = `/${newLang}${rest ? "/" + rest : "/"}`;
      navigate(target + location.search);
    };

    return { lang, t, lp, setLang };
  }, [lang, pathSegments, pathLang, navigate, location.search]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used inside <I18nProvider>");
  return ctx;
}
